import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, ArrowRight, Flame } from "lucide-react";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ASSETS } from "@/lib/assets";

const PRIZE_CARDS = [
  { n: "01", title: "1 PALABRA PRO CORRECTA", sub: "Participas por el PREMIO #1" },
  { n: "02", title: "2 PALABRAS PRO CORRECTAS", sub: "Participas por el PREMIO #2" },
  { n: "03", title: "3 PALABRAS PRO CORRECTAS", sub: "Participas por el GRAN PREMIO", highlight: true },
];

const emptyForm = () => ({
  full_name: "", email: "", phone: "",
  ep1: "", ep2: "", ep3: "",
  consent: false,
  website_backup: "",
});

const formatMiamiDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-US", {
      day: "numeric", month: "long", timeZone: "America/New_York",
    });
  } catch { return ""; }
};

export default function Reto() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "El Reto Modo Pro — Modo Pro Podcast";
    api.get("/reto/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const windowState = useMemo(() => {
    if (!cfg?.start_at || !cfg?.end_at) return "open";
    const now = Date.now();
    const s = new Date(cfg.start_at).getTime();
    const e = new Date(cfg.end_at).getTime();
    if (!cfg.enabled) return "closed";
    if (Number.isFinite(s) && now < s) return "upcoming";
    if (Number.isFinite(e) && now > e) return "closed";
    return "open";
  }, [cfg]);

  const startStr = formatMiamiDate(cfg?.start_at);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const validPhone = (form.phone || "").replace(/\D/g, "").length >= 7;
  const atLeastOne = [form.ep1, form.ep2, form.ep3].some((s) => (s || "").trim());
  const canSubmit = windowState === "open" && form.full_name.trim().length >= 2 && validEmail && validPhone && atLeastOne && form.consent && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/reto/submit", form);
      setDone(true);
      setForm(emptyForm());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos registrar tu participación");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main data-testid="reto-success" className="min-h-screen bg-mp-black pt-32 md:pt-40 pb-24">
        <div className="mx-auto max-w-[800px] px-6 lg:px-12 text-center">
          <div className="text-5xl md:text-6xl">🔥</div>
          <h1 className="mt-6 font-display font-bold text-white text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]">
            ¡Ya estás <span className="text-mp-orange">participando</span>!
          </h1>
          <p className="mt-8 text-white/80 text-lg leading-relaxed">
            Hemos recibido tus respuestas. Los ganadores serán anunciados en{" "}
            <a href={cfg?.youtube_url || "https://instagram.com/modopropodcast"} target="_blank" rel="noreferrer" className="text-mp-orange mp-link-underline">@modopropodcast</a>.
          </p>
          <p className="mt-10 font-display text-white text-xl">
            ¡Mucha suerte y activa tu <span className="text-mp-orange">Modo Pro</span>!
          </p>
          <Link to="/" className="mp-btn-ghost mt-12 inline-flex">Volver al sitio</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="reto-page" className="min-h-screen bg-mp-black">
      <Toaster theme="dark" />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={ASSETS.studioEmpty} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-mp-black" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1200px] px-6 lg:px-12 text-center">
          <div className="font-display text-5xl md:text-6xl">🔥</div>
          <div className="mp-eyebrow mt-6">El Reto Modo Pro</div>
          <h1 className="mt-4 font-display font-bold text-white text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-[-0.03em]">
            El Reto <span className="text-mp-orange">Modo Pro</span>
          </h1>
          <p data-testid="reto-promo" className="mt-8 font-display text-mp-orange text-2xl md:text-4xl tracking-[-0.015em]">
            {cfg?.promo_headline || "Hasta $1,000 en premios"}
          </p>
          <p className="mt-2 font-display text-white/80 text-sm md:text-base tracking-[0.24em] uppercase">
            {cfg?.promo_sub || "De nuestros patrocinadores"}
          </p>
          <p className="mt-8 text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Escucha nuestros 3 primeros episodios en YouTube y descubre la{" "}
            <span className="text-mp-orange font-semibold">Palabra Pro</span> de cada episodio.
          </p>
        </div>
      </section>

      {/* Prize cards */}
      <section data-testid="reto-prizes" className="py-16 md:py-24 border-t border-white/10 bg-mp-off">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
          <h2 className="font-display font-bold text-white text-3xl md:text-5xl leading-[1] tracking-[-0.02em] text-center">
            🎁 ¿Cuántas puedes <span className="text-mp-orange">encontrar</span>?
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRIZE_CARDS.map((c) => (
              <div key={c.n} data-testid={`reto-prize-${c.n}`} className={`relative border p-8 ${c.highlight ? "border-mp-orange bg-gradient-to-b from-mp-orange/15 to-transparent" : "border-white/10 bg-mp-black"}`}>
                {c.highlight && <div className="absolute -top-3 left-6 bg-mp-orange text-mp-black font-display text-[10px] tracking-[0.24em] uppercase px-3 py-1 flex items-center gap-1"><Flame size={11}/> Gran Premio</div>}
                <div className={`font-display text-xs tracking-[0.24em] uppercase ${c.highlight ? "text-mp-orange" : "text-mp-mute"}`}>Premio {c.n}</div>
                <div className="mt-5 font-display text-white text-2xl md:text-3xl leading-tight tracking-[-0.015em]">{c.title}</div>
                <div className="mt-3 text-white/70 text-sm">{c.sub}</div>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-white/70 text-base">
            <span className="text-mp-orange font-semibold">No necesitas encontrar las 3</span> para participar.
          </p>
        </div>
      </section>

      {/* YouTube CTA */}
      <section className="py-16 md:py-24 border-t border-white/10">
        <div className="mx-auto max-w-[900px] px-6 lg:px-12 text-center">
          <h2 className="font-display font-bold text-white text-3xl md:text-5xl leading-[1] tracking-[-0.02em]">
            🎧 ¿Te falta <span className="text-mp-orange">alguna</span>?
          </h2>
          <p className="mt-4 text-white/70">Escucha los 3 primeros episodios y encuentra cada Palabra Pro.</p>
          <a data-testid="reto-youtube" href={cfg?.youtube_url || "https://www.youtube.com/@ModoProPodcast"} target="_blank" rel="noreferrer" className="mp-btn-primary mt-10 inline-flex">
            <Youtube size={16}/> Ver los episodios en YouTube
          </a>
        </div>
      </section>

      {/* State banner / Form */}
      <section data-testid="reto-form" className="py-16 md:py-24 border-t border-white/10 bg-mp-off">
        <div className="mx-auto max-w-[760px] px-6 lg:px-12">
          {windowState === "upcoming" && (
            <div data-testid="reto-upcoming" className="text-center border border-mp-orange/50 p-10 md:p-14 bg-mp-orange/5">
              <div className="text-5xl">🔥</div>
              <h2 className="mt-4 font-display font-bold text-white text-2xl md:text-4xl leading-[1.05] tracking-[-0.015em] uppercase">
                El Reto comienza{startStr ? ` el ${startStr}` : " pronto"}
              </h2>
              <p className="mt-6 text-white/80 text-base">
                Vuelve el {startStr || "13 de octubre"} para enviar tus respuestas. Mientras tanto escucha los 3 primeros episodios en YouTube.
              </p>
            </div>
          )}

          {windowState === "closed" && (
            <div data-testid="reto-closed" className="text-center border border-white/20 p-10 md:p-14 bg-mp-black">
              <div className="text-5xl">🔥</div>
              <h2 className="mt-4 font-display font-bold text-white text-2xl md:text-4xl leading-[1.05] tracking-[-0.015em] uppercase">
                Participaciones cerradas
              </h2>
              <p className="mt-6 text-white/80 text-base">
                Gracias a todos los que participaron. Los ganadores se anunciarán en{" "}
                <a href="https://instagram.com/modopropodcast" target="_blank" rel="noreferrer" className="text-mp-orange mp-link-underline">@modopropodcast</a>.
              </p>
            </div>
          )}

          {windowState === "open" && (
            <>
              <div className="text-5xl text-center">🔥</div>
              <h2 className="mt-4 text-center font-display font-bold text-white text-3xl md:text-5xl leading-[1] tracking-[-0.02em]">
                Participa
              </h2>
              <div className="mt-12 space-y-7">
                <F label="Nombre y apellido *"><input data-testid="f-full_name" className="mp-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></F>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  <F label="Email *"><input data-testid="f-email" type="email" className="mp-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></F>
                  <F label="Teléfono *"><input data-testid="f-phone" className="mp-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></F>
                </div>
                {/* Honeypot */}
                <input type="text" autoComplete="off" tabIndex={-1} aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }} value={form.website_backup} onChange={(e) => set("website_backup", e.target.value)} />

                <div className="border-t border-white/10 pt-7 space-y-6">
                  {["ep1", "ep2", "ep3"].map((k, i) => (
                    <div key={k}>
                      <div className="mp-eyebrow">Episodio {i + 1}</div>
                      <div className="mt-2 font-display text-white text-lg">¿Cuál es la Palabra Pro?</div>
                      <input data-testid={`f-${k}`} className="mp-input mt-2" value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder="Escríbela aquí" />
                    </div>
                  ))}
                </div>

                <label className="flex items-start gap-4 cursor-pointer border border-white/10 p-5 bg-mp-black">
                  <input data-testid="f-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 accent-mp-orange w-4 h-4" />
                  <span className="text-white/80 text-sm leading-relaxed">
                    He leído y acepto los <Link to="/reto/reglas" className="text-mp-orange mp-link-underline">términos y condiciones</Link> y el aviso de privacidad del concurso.
                  </span>
                </label>

                <button data-testid="reto-submit" disabled={!canSubmit} onClick={submit} className="mp-btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting ? "Enviando…" : <>Enviar mis respuestas <ArrowRight size={16}/></>}
                </button>
              </div>
            </>
          )}

          <div className="pt-8 text-mp-mute text-xs leading-relaxed space-y-3">
            <p>Los ganadores serán seleccionados al azar entre las participaciones con respuestas correctas y anunciados en @modopropodcast.</p>
            <p className="text-mp-orange font-semibold">Activa tu Modo Pro. 🔥</p>
            <p>Hasta $1,000 en valor total de premios proporcionados por nuestros patrocinadores. Los premios no son efectivo. Una participación por persona. Aplican términos y condiciones. <Link to="/reto/reglas" className="mp-link-underline text-white/80">Ver reglas oficiales</Link>.</p>
            <p className="pt-2 border-t border-white/10 text-mp-dim">Organiza: {cfg?.organizer_name || "Jupiter Productions LLC"} · {cfg?.organizer_brand || "Modo Pro Podcast"}</p>
          </div>
        </div>
      </section>
    </main>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="mp-label">{label}</div>
      {children}
    </div>
  );
}
